import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  IconButton,
  Toolbar,
  Typography,
  LinearProgress,
  Snackbar,
  Alert,
  Box,
  alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import { useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { TbLogs } from 'react-icons/tb';
import { MdCampaign } from 'react-icons/md';
import { MdOutlineQuickreply } from 'react-icons/md';
import { RiSurveyLine } from 'react-icons/ri';
import { FiUsers } from 'react-icons/fi';
import { AiOutlineDashboard } from 'react-icons/ai';

// Color palette que coincide con los KPI cards y la tabla
const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  purple: '#673ab7',
  background: '#f7f9fc',
  lightBorder: '#e3f2fd',
  hoverRow: alpha('#2196f3', 0.08)
};

const drawerWidth = 290;

const AppBar = styled(MuiAppBar)(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 1px 10px 0px rgba(0,0,0,0.025)',
  backgroundColor: '#ffffff',
  borderBottom: `1px solid ${COLORS.lightBorder}`,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#424242',
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1.5),
    color: COLORS.primary,
  }
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  backgroundColor: alpha(COLORS.primary, 0.15),
  '& .MuiLinearProgress-bar': {
    backgroundColor: COLORS.primary,
  }
}));

const styles = {
  logo: {
    height: 50,
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
    }
  },
};

export default function Navbar(props) {
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const navigate = useNavigate();

  const { open, handleDrawer, isLoading, snack, setSnackAlert, profile } = props;
  const tenant_id = profile?.tenant_id;

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    const newSnack = { open: false, severity: 'success', message: '' };
    setSnackAlert(newSnack);
  };
  useEffect(() => {
    if (snack.open) {
      setSnackBarOpen(true);
    } else {
      setSnackBarOpen(false);
    }
  }, [snack]);

  const location = useLocation();

  // Función para determinar el ícono según la ruta
  const getIcon = () => {
    const path = location.pathname.split('/').pop();
    switch(path) {
      case 'logs':
        return <TbLogs size={22} />;
      case 'marketing':
        return <MdCampaign size={22} />;
      case 'respuestas':
        return <MdOutlineQuickreply size={22} />;
      case 'formularios':
        return <RiSurveyLine size={22} />;
      case 'tenant-users':
        return <FiUsers size={22} />;
      case 'dashboard':
        return <DashboardIcon />;
      case 'marketing-dashboard':
        return <AiOutlineDashboard size={22} />;
      case 'chats':
        return <WhatsAppIcon />;
      case 'landing':
        return <HomeIcon />;
      default:
        return <HomeIcon />;
    }
  };

  // Función para determinar el título según la ruta y el tenant_id
  const getPageTitle = () => {
    const path = location.pathname.split('/').pop();
    
    switch(path) {
      case 'logs':
        return `Logs de Robnett ${profile.tenant_name}`;
      case 'marketing':
        return 'Envío de mensajes masivos';
      case 'respuestas':
        return 'Respuestas del formulario de contacto';
      case 'formularios':
        return 'Formularios';
      case 'tenant-users':
        return 'Gestión de usuarios';
      case 'dashboard':
        return 'Dashboard';
      case 'marketing-dashboard':
        return 'Dashboard mensajeria masiva';
      case 'chats':
        return 'Chat';
      case 'landing':
        return `Robnett ${profile.tenant_name}`;
      case '':
        // Página inicial - varía según tenant_id
        if (tenant_id === 1) {
          return 'Dashboard';
        } else if (tenant_id === 5) {
          return `Robnett ${profile.tenant_name}`;
        } else {
          return 'Dashboard';
        }
      default:
        return `Robnett ${profile.tenant_name}`;
    }
  };

  return (
    <AppBar position="fixed" open={open}>
      <Toolbar sx={{ px: 2 }}>
        <Box 
          component="img"
          sx={{
            ...styles.logo,
            cursor: 'pointer',
            '&:hover': {
              transform: 'scale(1.02)'
            }
          }}
          src={profile.tenant_logo}
          alt="logo"
          onClick={() => {
            navigate('/');
          }}
        />

        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <PageTitle 
            variant="h6" 
            fontSize={20}
            component="div"
          >
            {getIcon()}
            {getPageTitle()}
          </PageTitle>
        </Box>
      </Toolbar>
      {isLoading && <ProgressBar />}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={6000}
        onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity={snack.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.1)'
          }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </AppBar>
  );
}
